import { Fragment, ReactNode } from 'react'
import Skeleton from '@mui/material/Skeleton'
import Grid from '@mui/material/Grid'
import { ExpandMoreSharp } from '@mui/icons-material'
import { range } from 'libraries/lodash'
import { DEFAULT_AOL_ORDER, AOL_DISPLAY_NAMES } from 'constants/aol/aol'
import TileButton from 'components/tiled-menu/TileButton'
import { AOLType } from 'types/AOL'

interface TiledMenuSkeletonProps {
  tileCountPerAol?: number
  showAolSections?: boolean
}

const SkeletonTile = (): JSX.Element => (
  <div className='bg-white tile-border h8 relative overflow-hidden'>
    <TileButton
      classes={{ label: 'h-100 wait', root: 'h-100' }}
      disableRipple
      fullWidth
    >
      <div className='absolute pt5 ph5 top0 left0 right0'>
        <Skeleton width='90%' height={18} className='mb1' />
      </div>
      <div className='absolute flex items-end justify-start ph5 gap5 bottom4 left0'>
        <Skeleton variant='circular' width={24} height={24} />
        <Skeleton variant='circular' width={24} height={24} />
      </div>
    </TileButton>
  </div>
)

interface SkeletonTilesProps {
  tileCount: number
}

const SkeletonTiles = ({ tileCount = 8 }: SkeletonTilesProps): JSX.Element => (
  <Grid container spacing={2}>
    {range(0, tileCount).map((_, index) => (
      <Grid key={index} item xs={12} sm={4} md={3} lg={2}>
        <SkeletonTile />
      </Grid>
    ))}
  </Grid>
)

interface AolSectionSkeletonProps {
  aol: string
  tileCount: number
  showHeader?: boolean
}

const AolSectionSkeleton = ({
  aol,
  tileCount,
  showHeader = true
}: AolSectionSkeletonProps): JSX.Element => (
  <div>
    {showHeader && (
      <div className='flex cadet-blue pb4 mb5 custom-disclosure-header items-center no-select'>
        <div className='flex items-center'>
          <span className='flex items-center mlauto transition-all'>
            <ExpandMoreSharp className={`white br2 bg-aol-${aol}`} />
          </span>
          <div className='w-100'>
            <h2 className='black di ml4 f4 normal'>
              {AOL_DISPLAY_NAMES[aol as AOLType]} Law
            </h2>
          </div>
        </div>
      </div>
    )}
    <nav>
      <SkeletonTiles tileCount={tileCount} />
    </nav>
  </div>
)

const TiledMenuSkeleton = ({
  tileCountPerAol = 20,
  showAolSections = false
}: TiledMenuSkeletonProps): JSX.Element => {
  const content: ReactNode = showAolSections
    ? (
        DEFAULT_AOL_ORDER.map((aol, index, arr) => (
          <Fragment key={`${aol}-skeleton`}>
            <AolSectionSkeleton aol={aol} tileCount={tileCountPerAol} showHeader />
            {index < arr.length - 1 && <hr className='mt7 grey bg-grey' />}
          </Fragment>
        ))
      )
    : (
      <div className='tile-container mt6'>
        <SkeletonTiles tileCount={tileCountPerAol} />
      </div>
      )

  return (
    <div className='bg-page-background'>
      <div className='layout-width-constraint pt8 pa7'>
        {content}
      </div>
    </div>
  )
}

export default TiledMenuSkeleton
